import { definePolicy, type Policy } from '../kernel';
import { type Action, allow, type Decision, deny } from '../types';
import type { ProductionRunStatus } from './productionRun';

export type ProductionRunComponentFacts = {
  productionRunStatus: ProductionRunStatus;
  productionRunIsTemplate: boolean;
};

/**
 * The component values a production run surface can write. An input's quantity, culled weight
 * and pallet tags; an output's produced quantity and weight.
 */
export const PRODUCTION_RUN_COMPONENT_FIELDS = [
  'inputQuantity',
  'inputWeightDumped',
  'inputPalletTags',
  'outputQuantity',
  'outputWeight',
] as const;

export type ProductionRunComponentField = (typeof PRODUCTION_RUN_COMPONENT_FIELDS)[number];

/** The statuses in which components (and scanned pallet tags) can be added to or removed from a run. */
export const PRODUCTION_RUN_COMPONENTS_EDITABLE_STATUSES: readonly ProductionRunStatus[] = ['DRAFT', 'IN_PROGRESS'];

type ComponentAction = Action<ProductionRunComponentField, never>;

const decideEntity = (facts: ProductionRunComponentFacts, action: ComponentAction): Decision => {
  switch (action.type) {
    case 'view':
    case 'edit':
    case 'perform':
      return allow();
    case 'add':
    case 'delete':
      return PRODUCTION_RUN_COMPONENTS_EDITABLE_STATUSES.includes(facts.productionRunStatus)
        ? allow()
        : deny('PRODUCTION_RUN_COMPONENTS_LOCKED', "Completed production runs can't be edited.");
  }
};

/** An input's values change only while the run is in progress. */
const inputRule = (facts: ProductionRunComponentFacts, action: ComponentAction): Decision =>
  action.type !== 'edit' || facts.productionRunStatus === 'IN_PROGRESS'
    ? allow()
    : deny('PRODUCTION_RUN_INPUT_LOCKED', 'Inputs can be edited only while the run is In Progress.');

/** An output's values change while the run is in progress — or on a template, whose captured packing is edited on the card. */
const outputRule = (facts: ProductionRunComponentFacts, action: ComponentAction): Decision =>
  action.type !== 'edit' || facts.productionRunStatus === 'IN_PROGRESS' || facts.productionRunIsTemplate
    ? allow()
    : deny('PRODUCTION_RUN_OUTPUT_LOCKED', 'Outputs can be edited only while the run is In Progress.');

const fieldRules = {
  inputQuantity: inputRule,
  inputWeightDumped: inputRule,
  inputPalletTags: inputRule,
  outputQuantity: outputRule,
  outputWeight: outputRule,
} satisfies Record<ProductionRunComponentField, typeof inputRule>;

export const productionRunComponentPolicy: Policy<ProductionRunComponentFacts, ProductionRunComponentField, never> =
  definePolicy<ProductionRunComponentFacts, ProductionRunComponentField, never>({
    kind: 'production run component',
    fields: PRODUCTION_RUN_COMPONENT_FIELDS,
    ops: [],
    requires: {},
    entity: decideEntity,
    field: fieldRules,
  });
